import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, Messaging } from 'firebase/messaging';
import { HttpClient } from '@angular/common/http';
import { environment } from 'environments/environment';
import { CurrentService } from './current.service';
import { NotificationsService } from 'app/layout/components/navbar/navbar-notification/notifications.service';
import { Notifications } from './models/notification.model';

@Injectable({
  providedIn: 'root'
})
export class FirebaseMessagingService {
  private messaging: Messaging;

  constructor(
    private http: HttpClient,
    private currentService: CurrentService,
    private notificationsService: NotificationsService,
    private router: Router
  ) {
    const app = initializeApp(environment.firebase);
    this.messaging = getMessaging(app);

    // Enregistrer le service worker
    this.registerServiceWorker();

    // Écouter les messages de navigation du service worker
    this.setupServiceWorkerMessageListener();
  }

  private async registerServiceWorker(): Promise<void> {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register(
          `/firebase-messaging-sw${environment.production ? '' : '.dev'}.js`
        );
        console.log('✅ Service Worker registered:', registration.scope);

        // Attendre que le service worker soit prêt
        await navigator.serviceWorker.ready;
        console.log('✅ Service Worker is ready');
      } catch (error) {
        console.error('❌ Service Worker registration failed:', error);
      }
    } else {
      console.warn('⚠️ Service Workers are not supported in this browser');
    }
  }

  async requestPermission(): Promise<string | null> {
    try {
      const permission = await Notification.requestPermission();

      if (permission === 'granted') {
        console.log('✅ Notification permission granted');

        // Attendre que le service worker soit prêt
        if ('serviceWorker' in navigator) {
          await navigator.serviceWorker.ready;
        }

        const token = await getToken(this.messaging, {
          vapidKey: environment.vapikey
        });

        console.log('✅ FCM Token generated:', token);

        // Envoyer token à l'API
        await this.updateFcmToken(token);

        return token;
      } else {
        console.warn('⚠️ Notification permission denied');
        return null;
      }
    } catch (error) {
      console.error('❌ Error getting FCM token:', error);
      return null;
    }
  }

  private async updateFcmToken(token: string): Promise<void> {
    try {
      const currentUser = this.currentService.currentUser.getValue();
      if (!currentUser?.id) {
        console.warn('⚠️ No user logged in, cannot save FCM token');
        return;
      }

      await this.http
        .post(`${environment.apiUrl}/api/Auth/update-fcm-token/${currentUser.id}`, {
          fcmToken: token,
          platform: 'Web',
          appVersion: '1.0.0'
        })
        .toPromise();

      console.log('✅ FCM Token saved to server');
    } catch (error) {
      console.error('❌ Error saving FCM token:', error);
    }
  }

  listenToMessages(): void {
    console.log('👂 Setting up foreground message listener...');

    onMessage(this.messaging, payload => {
      console.log('📩 ════════════════════════════════════════');
      console.log('📩 FOREGROUND Message Received!');
      console.log('📩 ════════════════════════════════════════');
      console.log('Title:', payload.notification?.title);
      console.log('Body:', payload.notification?.body);
      console.log('Data:', payload.data);
      console.log('📩 ════════════════════════════════════════');

      const notificationTitle = payload.notification?.title || 'Jeto';
      const notificationBody = payload.notification?.body || '';

      if (Notification.permission === 'granted') {
        console.log('✅ Creating notification...');
        try {
          const notification = new Notification(notificationTitle, {
            body: notificationBody,
            icon: '/assets/images/logo/logo.png',
            badge: '/assets/images/logo/favicon.ico',
            requireInteraction: true,
            tag: 'utrav-notification',
            data: payload.data // Store data for onclick handler
          });

          console.log('✅ Notification displayed successfully!');

          notification.onclick = () => {
            console.log('🖱️ ════════════════════════════════════════');
            console.log('🖱️ NOTIFICATION CLIQUÉE (Foreground)');
            console.log('🖱️ ════════════════════════════════════════');
            console.log('📦 Data Payload:', payload.data);

            // Build notification object from FCM data
            const notificationData: Notifications = {
              id: payload.data?.notificationId || '',
              title: notificationTitle,
              message: notificationBody,
              actionUrl: payload.data?.actionUrl || '',
              type: payload.data?.type || '',
              status: 'Sent',
              tripId: payload.data?.tripId || null,
              bookingId: payload.data?.bookingId || null,
              paymentId: payload.data?.paymentId || null,
              loyaltyTransactionId: payload.data?.loyaltyTransactionId || null,
              isRead: false,
              createdAt: new Date(),
              updatedAt: new Date(),
              userId: this.currentService.currentUser.getValue()?.id || '',
              user: null as any,
              booking: null as any,
              trip: null as any,
              payment: null as any
            };

            console.log('🚀 Appel de navigateToNotificationTarget...');
            console.log('📍 ActionUrl:', notificationData.actionUrl);
            console.log('📦 Type:', notificationData.type);

            // Focus window and close notification
            window.focus();
            notification.close();

            // Navigate using NotificationsService
            this.notificationsService.navigateToNotificationTarget(notificationData);

            console.log('✅ Navigation déclenchée avec succès');
            console.log('🖱️ ════════════════════════════════════════');
          };

          notification.onerror = error => {
            console.error('❌ Notification error:', error);
          };
        } catch (error) {
          console.error('❌ Error creating notification:', error);
        }
      } else {
        console.error('❌ Notification permission not granted! Current:', Notification.permission);
      }
    });

    console.log('✅ Foreground message listener configured');
  }

  private setupServiceWorkerMessageListener(): void {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', event => {
        console.log('📨 ════════════════════════════════════════');
        console.log('📨 Message reçu du Service Worker');
        console.log('📨 ════════════════════════════════════════');
        console.log('Type:', event.data?.type);
        console.log('URL:', event.data?.url);

        if (event.data?.type === 'NAVIGATE' && event.data?.url) {
          console.log('🚀 Navigation vers:', event.data.url);

          // Remove leading slash if present for Angular router
          const route = event.data.url.startsWith('/')
            ? event.data.url.substring(1)
            : event.data.url;

          // Check if URL has query params
          if (route.includes('?')) {
            const [path, queryString] = route.split('?');
            const queryParams: any = {};
            queryString.split('&').forEach(param => {
              const [key, value] = param.split('=');
              queryParams[key] = value;
            });
            console.log('📍 Navigation avec query params:', path, queryParams);
            this.router.navigate([path], { queryParams });
          } else {
            console.log('📍 Navigation simple:', route);
            this.router.navigate([route]);
          }

          console.log('✅ Navigation effectuée');
        }
        console.log('📨 ════════════════════════════════════════');
      });

      console.log('✅ Service Worker message listener configured');
    }
  }
}
